import { Injectable, inject } from '@angular/core';

import { ApiService } from '../api/api.service';
import { ROOT_SCOPE_PORT, type IRootScopePort } from '../utils/root-scope.port';

export type ConfigType = 'app' | 'services' | 'directives' | 'factories' | 'views';

export interface PreferenceItem {
  id: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
}

export interface PreferencesContainer {
  preferences: PreferenceItem[];
  [key: string]: unknown;
}

export interface ConfigSettingsProps<T = unknown> {
  type: ConfigType | string;
  name: string;
  value: T;
}

export interface SetPreferencesByKeyProps {
  type: ConfigType | string;
  name: string;
  key: string;
  configType?: string;
  value: Record<string, unknown>;
}

interface UserPreferencesApi {
  update(id: string, props: unknown, userId?: string): PromiseLike<unknown>;
}

/**
 * ConfigService (Angular 18).
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/config/config.service.js
 *
 * Exposed back to AngularJS via `downgradeInjectable` under the legacy name
 * `'ConfigService'`, so existing consumers in b2b-ui keep working without
 * modification (see ng-downgraded-components.ts).
 */
@Injectable({ providedIn: 'root' })
export class ConfigService {
  private api = inject(ApiService);
  private rootScope = inject<IRootScopePort>(ROOT_SCOPE_PORT);

  settings: Record<string, Record<string, unknown>> = {
    app: {},
    services: {},
    directives: {},
    factories: {},
    views: {},
  };

  settingsTypes = {
    app: 'apps',
    service: 'services',
    directive: 'directives',
    view: 'views',
  };

  private userPreferenceConfigIds: Record<string, Record<string, Record<string, Record<string, string>>>> = {
    views: {
      'application.orders.list': {
        grid: {
          'application.orders.list.grid0': 'efe7c293-5e80-4879-8310-bad55875aa59',
        },
      },
    },
  };

  init(settings?: ConfigSettingsProps | ConfigSettingsProps[]): boolean {
    if (!settings) {
      return true;
    }
    if (Array.isArray(settings)) {
      for (const item of settings) {
        if (!this.set(item)) {
          return false;
        }
      }
      return true;
    }
    return this.set(settings);
  }

  getConfigId(type: string, name: string, configType: string, configName: string): string {
    if (type && name && configType && configName) {
      const byType = this.userPreferenceConfigIds[type];
      const byName = byType?.[name];
      const byConfigType = byName?.[configType];
      const id = byConfigType?.[configName];
      if (id) {
        return id;
      }
    }
    return this.newUuid();
  }

  setConfigId(type: string, name: string, config: PreferenceItem): void {
    if (!type || !name || !config?.type || !config?.name) {
      return;
    }
    const byType = (this.userPreferenceConfigIds[type] ??= {});
    const byName = (byType[name] ??= {});
    const byConfigType = (byName[config.type] ??= {});
    byConfigType[config.name] = config.id;
  }

  get<T = unknown>(type: string, name: string): T | false {
    if (type && name && this.settings[type] && Object.prototype.hasOwnProperty.call(this.settings[type], name)) {
      return this.settings[type][name] as T;
    }
    return false;
  }

  getUserPreferences(type: string, name: string): PreferenceItem[] | false {
    if (type && name && this.settings[type]) {
      const entry = this.settings[type][name] as PreferencesContainer | undefined;
      if (entry && Array.isArray(entry.preferences)) {
        return entry.preferences;
      }
    }
    return false;
  }

  getPreferencesByKey(type: string, name: string, key: string): Record<string, unknown> | false {
    if (type && name && this.settings[type]) {
      const entry = this.settings[type][name] as PreferencesContainer | undefined;
      if (entry && Array.isArray(entry.preferences)) {
        for (const pref of entry.preferences) {
          if (pref.name === key) {
            const cfg = pref.config;
            if (this.isEmpty(cfg)) {
              return false;
            }
            return cfg;
          }
        }
      }
    }
    return false;
  }

  isPreferencesLoadedForKey(type: string, name: string, _key?: string): boolean {
    if (type && name && this.settings[type]) {
      const entry = this.settings[type][name] as PreferencesContainer | undefined;
      if (entry && Array.isArray(entry.preferences)) {
        return true;
      }
    }
    return false;
  }

  isEmpty(prop: unknown): boolean {
    return (
      !prop ||
      (typeof prop === 'object' &&
        Object.keys(prop as object).length === 0 &&
        JSON.stringify(prop) === JSON.stringify({}))
    );
  }

  set(props: ConfigSettingsProps): boolean {
    if (props && props.type && props.name && props.value !== undefined && props.value !== null) {
      if (Object.prototype.hasOwnProperty.call(this.settings, props.type)) {
        this.settings[props.type][props.name] = props.value;
        return true;
      }
    }
    return false;
  }

  setPreferences(props: ConfigSettingsProps<PreferenceItem[]>): boolean {
    if (props && props.type && props.name && props.value) {
      const preferences = props.value || [];
      if (Object.prototype.hasOwnProperty.call(this.settings, props.type)) {
        this.settings[props.type][props.name] = { preferences };
        for (const pref of preferences) {
          this.setConfigId(props.type, props.name, pref);
        }
        return true;
      }
    }
    return false;
  }

  setPreferencesByKey(props: SetPreferencesByKeyProps): boolean {
    if (!(props && props.type && props.name && props.key && props.value)) {
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(this.settings, props.type)) {
      return false;
    }

    const updatedConfig: PreferenceItem = {
      id: this.getConfigId(props.type, props.name, props.configType ?? '', props.key),
      name: props.key,
      type: props.configType ?? '',
      config: props.value || {},
    };

    if (!Object.prototype.hasOwnProperty.call(this.settings[props.type], props.name)) {
      this.set({ type: props.type, name: props.name, value: {} });
    }

    const entry = this.settings[props.type][props.name] as PreferencesContainer;
    if (!Array.isArray(entry.preferences)) {
      entry.preferences = [];
    }

    let found = false;
    for (const pref of entry.preferences) {
      if (pref.name === props.key) {
        pref.config = updatedConfig.config;
        found = true;
        break;
      }
    }
    if (!found) {
      entry.preferences.push({ ...updatedConfig });
    }
    return true;
  }

  dotsToDashes(name: string): string {
    return name.replace(/\./g, '-');
  }

  clear(type: string, name: string, remove?: boolean): boolean {
    if (type && name && this.settings[type] && Object.prototype.hasOwnProperty.call(this.settings[type], name)) {
      this.settings[type][name] = null as unknown as Record<string, unknown>;
      if (remove) {
        delete this.settings[type][name];
      }
      return true;
    }
    return false;
  }

  clearPreferencesByKey(type: string, name: string, key: string, remove?: boolean): boolean | Record<string, unknown> {
    if (!(type && name && this.settings[type])) {
      return false;
    }
    const entry = this.settings[type][name] as PreferencesContainer | undefined;
    if (!(entry && Array.isArray(entry.preferences))) {
      return false;
    }
    for (let i = 0; i < entry.preferences.length; i++) {
      if (entry.preferences[i].name === key) {
        if (remove) {
          entry.preferences.splice(i, 1);
          return false;
        }
        entry.preferences[i].config = {};
        return entry.preferences[i].config;
      }
    }
    return false;
  }

  save(props: { type: string; name: string }): void {
    const { type, name } = props;
    if (!name) {
      return;
    }
    if (!(this.settings[type] && Object.prototype.hasOwnProperty.call(this.settings[type], name))) {
      return;
    }
    const userPreferences = this.api.userPreferences as UserPreferencesApi;
    const broadcastSaved = () => {
      this.rootScope.$broadcast('ConfigService::SettingsSaved', { name });
    };
    userPreferences.update(this.dotsToDashes(name), this.settings[type][name]).then(broadcastSaved, broadcastSaved);
  }

  private newUuid(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
